import {
  type FocusEvent,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { cleanCptCodes, CPT_CODES, type CPTCodeOption } from '@components/ui/forms/CPTCodePicker';
import { SpecialtySelect } from '@components/ui/forms/SpecialtySelect';
import { type AutoSaveStatus, useAutoSave } from '@hooks/useAutoSave';
import { Close } from '@mui/icons-material';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { getStateOptions, US_STATES } from '@shared/constants/states';
import { isBillableCodeShape } from '@utils/billingCode';

import { displayIntakeFieldValue } from '../../fieldDisplay';
import { getIntakeFieldFormatError, isPhoneOrFaxField } from '../../fieldFormatValidation';
import {
  type SuggestionAcceptanceFailureKind,
  suggestionAcceptanceFailureKind,
} from '../../suggestionAcceptance';
import type { GeneratedIntakeField, IntakeFieldSuggestion, IntakeFieldValue } from '../../types';

import { wf } from './tokens';

interface IntakePageFieldInputProps {
  field: GeneratedIntakeField;
  draftValue: IntakeFieldValue;
  acknowledgedValue: IntakeFieldValue;
  hasAcknowledgedValue?: boolean;
  onDraftChange: (field: GeneratedIntakeField, value: IntakeFieldValue) => void;
  onPersist: (field: GeneratedIntakeField, value: IntakeFieldValue) => Promise<void>;
  suggestion?: IntakeFieldSuggestion;
  onAcceptSuggestion?: (suggestion: IntakeFieldSuggestion) => Promise<void>;
  onDiscardSuggestion?: (suggestion: IntakeFieldSuggestion) => void;
  disabled?: boolean;
  disabledReason?: string;
  /**
   * Render the input as visibly non-editable while keeping the suggestion
   * accept/discard controls live — unlike `disabled`, which also gates them.
   */
  readOnly?: boolean;
  /**
   * Server-masked display form of a secure value already on file. The plaintext
   * never reaches the client, so this is all the UI can show.
   */
  maskedValue?: string;
  saveMode?: 'autosave' | 'explicit';
  showStatus?: boolean;
  saveStateKey?: string;
  onSaveStateChange?: (
    field: GeneratedIntakeField,
    state: IntakeFieldSaveState,
    saveStateKey?: string,
  ) => void;
  validationError?: string;
}

export interface IntakeFieldSaveState {
  status: AutoSaveStatus;
  isDirty: boolean;
  lastSavedAt: Date | null;
  retry: () => void;
}

const IMMEDIATE_SAVE_TYPES = new Set(['boolean']);
// Digits plus the separators format validation already tolerates: space, (), ., -.
const PHONE_ALLOWED_CHAR = /[\d\s().-]/;
const PHONE_DISALLOWED_CHARS = /[^\d\s().-]/g;
const SECRET_TYPES = new Set(['secret', 'ssn']);
const COMMON_CPT_CODES_FIELD_KEY = 'common_cpt_codes';
const STATE_OPTIONS = getStateOptions();
const SPECIALTY_FIELD_ID = 'I18.2';
const EDITABLE_CONTROL_SELECTOR = [
  'input:not([type="hidden"]):not(:disabled):not([aria-hidden="true"]):not([tabindex="-1"])',
  'textarea:not(:disabled)',
  '[role="combobox"]:not([aria-disabled="true"])',
].join(', ');
const EMPTY_GENERATED_DATE_SX = {
  '& input[data-empty-generated-date="true"]:not(:focus)::-webkit-datetime-edit': {
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
  },
} as const;
// A readOnly-but-not-disabled look for controls (e.g. SpecialtySelect) that
// don't accept a `readOnly` prop of their own — the disabled visual/pointer
// treatment without the `disabled` prop, which would also gate the
// suggestion accept/discard controls (see suggestionControlsDisabled).
const READONLY_WRAPPER_SX = { pointerEvents: 'none', opacity: 0.72 } as const;

function fieldControlType(dataType: string): 'date' | 'number' | 'password' | 'text' {
  if (dataType === 'date') return 'date';
  if (dataType === 'integer' || dataType === 'number') return 'number';
  if (SECRET_TYPES.has(dataType)) return 'password';
  return 'text';
}

function isSameValue(left: IntakeFieldValue, right: IntakeFieldValue): boolean {
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }
  return Object.is(left, right);
}

function hasDisplayedValue(value: IntakeFieldValue): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

function isCommonCptCodesField(field: GeneratedIntakeField): boolean {
  return (
    field.field_key === COMMON_CPT_CODES_FIELD_KEY ||
    field.destination.field_key === COMMON_CPT_CODES_FIELD_KEY
  );
}

function isSpecialtyField(field: GeneratedIntakeField): boolean {
  return (
    field.field_id === SPECIALTY_FIELD_ID &&
    field.requirement_key === 'organization.practice_profile' &&
    field.destination.field_key === 'specialty'
  );
}

function uniqueBillableCodes(codes: string[]): string[] {
  return [...new Set(cleanCptCodes(codes).filter(isBillableCodeShape))];
}

function uniqueStoredCodes(codes: string[]): string[] {
  const seen = new Set<string>();
  return codes.flatMap(code => {
    const trimmed = code.trim();
    const key = trimmed.toUpperCase();
    if (!trimmed || seen.has(key)) return [];
    seen.add(key);
    return [trimmed];
  });
}

function suggestionSource(documentKind: string): string {
  const label = documentKind.toUpperCase() === 'W9' ? 'W-9' : documentKind;
  return `${label} document`;
}

interface DiffToken {
  kind: 'added' | 'removed' | 'unchanged';
  value: string;
}

const MAX_DIFF_MATRIX_SIZE = 40_000;

function tokenizeDiffValue(value: string): string[] {
  return value.match(/\s+|[^\s]+/g) ?? [];
}

function diffWords(
  currentValue: string,
  suggestedValue: string,
): {
  current: DiffToken[];
  suggested: DiffToken[];
} {
  const currentTokens = tokenizeDiffValue(currentValue);
  const suggestedTokens = tokenizeDiffValue(suggestedValue);
  if (currentTokens.length * suggestedTokens.length > MAX_DIFF_MATRIX_SIZE) {
    return {
      current: currentTokens.map(value => ({ kind: 'removed', value })),
      suggested: suggestedTokens.map(value => ({ kind: 'added', value })),
    };
  }
  const commonTokenCounts = Array.from({ length: currentTokens.length + 1 }, () =>
    Array<number>(suggestedTokens.length + 1).fill(0),
  );

  for (let currentIndex = currentTokens.length - 1; currentIndex >= 0; currentIndex -= 1) {
    for (
      let suggestedIndex = suggestedTokens.length - 1;
      suggestedIndex >= 0;
      suggestedIndex -= 1
    ) {
      commonTokenCounts[currentIndex][suggestedIndex] =
        currentTokens[currentIndex] === suggestedTokens[suggestedIndex]
          ? commonTokenCounts[currentIndex + 1][suggestedIndex + 1] + 1
          : Math.max(
              commonTokenCounts[currentIndex + 1][suggestedIndex],
              commonTokenCounts[currentIndex][suggestedIndex + 1],
            );
    }
  }

  const current: DiffToken[] = [];
  const suggested: DiffToken[] = [];
  let currentIndex = 0;
  let suggestedIndex = 0;

  while (currentIndex < currentTokens.length && suggestedIndex < suggestedTokens.length) {
    if (currentTokens[currentIndex] === suggestedTokens[suggestedIndex]) {
      current.push({ kind: 'unchanged', value: currentTokens[currentIndex] });
      suggested.push({ kind: 'unchanged', value: suggestedTokens[suggestedIndex] });
      currentIndex += 1;
      suggestedIndex += 1;
    } else if (
      commonTokenCounts[currentIndex + 1][suggestedIndex] >=
      commonTokenCounts[currentIndex][suggestedIndex + 1]
    ) {
      current.push({ kind: 'removed', value: currentTokens[currentIndex] });
      currentIndex += 1;
    } else {
      suggested.push({ kind: 'added', value: suggestedTokens[suggestedIndex] });
      suggestedIndex += 1;
    }
  }

  while (currentIndex < currentTokens.length) {
    current.push({ kind: 'removed', value: currentTokens[currentIndex] });
    currentIndex += 1;
  }
  while (suggestedIndex < suggestedTokens.length) {
    suggested.push({ kind: 'added', value: suggestedTokens[suggestedIndex] });
    suggestedIndex += 1;
  }

  return { current, suggested };
}

function DiffValue({ tokens }: { tokens: DiffToken[] }) {
  return (
    <>
      {tokens.map((token, index) => {
        const isChanged = token.kind !== 'unchanged' && token.value.trim().length > 0;
        return (
          <Box
            component="span"
            data-diff={isChanged ? token.kind : undefined}
            key={`${index}-${token.value}`}
            sx={
              !isChanged
                ? undefined
                : token.kind === 'removed'
                  ? { backgroundColor: wf.redSoft, color: wf.red, borderRadius: '3px' }
                  : { backgroundColor: wf.greenSoft, color: wf.green, borderRadius: '3px' }
            }
          >
            {token.value}
          </Box>
        );
      })}
    </>
  );
}

/**
 * A single generated Intake field with isolated autosave state. Persistence is
 * supplied by the generated page definition so this component never needs to infer a backend
 * destination from display metadata.
 */
function IntakePageFieldInputComponent({
  field,
  draftValue,
  acknowledgedValue,
  hasAcknowledgedValue,
  onDraftChange,
  onPersist,
  suggestion,
  onAcceptSuggestion,
  onDiscardSuggestion,
  disabled = false,
  disabledReason,
  readOnly = false,
  maskedValue,
  saveMode = 'autosave',
  showStatus = true,
  saveStateKey,
  onSaveStateChange,
  validationError,
}: IntakePageFieldInputProps) {
  const [suggestionError, setSuggestionError] = useState<SuggestionAcceptanceFailureKind | null>(
    null,
  );
  const [isAcceptingSuggestion, setIsAcceptingSuggestion] = useState(false);
  const [isEditingSuggestion, setIsEditingSuggestion] = useState(false);
  const [blurValidationError, setBlurValidationError] = useState<string>();
  const [sessionSavedSecret, setSessionSavedSecret] = useState<string | null>(null);
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const [isReplacingSecret, setIsReplacingSecret] = useState(false);
  const controlRootRef = useRef<HTMLDivElement>(null);
  const editSuggestionButtonRef = useRef<HTMLButtonElement>(null);
  const cancelManualEditButtonRef = useRef<HTMLButtonElement>(null);
  const isCancellingManualEditRef = useRef(false);
  const shouldRestoreSuggestionFocusRef = useRef(false);
  const isEditingSuggestionRef = useRef(false);
  const manualEditBaselineRef = useRef<IntakeFieldValue | undefined>(undefined);
  const suggestionRef = useRef(suggestion);
  const discardSuggestionRef = useRef(onDiscardSuggestion);
  isEditingSuggestionRef.current = isEditingSuggestion;
  suggestionRef.current = suggestion;
  discardSuggestionRef.current = onDiscardSuggestion;
  const dataType = field.data_type.toLowerCase();
  const isSecret = SECRET_TYPES.has(dataType);
  const isSensitive = isSecret || field.destination.kind === 'intake_item_sensitive';
  const isNumeric = dataType === 'integer' || dataType === 'number';
  const isInteger = dataType === 'integer';
  const isPhoneOrFax = isPhoneOrFaxField(field);
  const isWritable = field.is_writable !== false;
  const isDirty = !isSameValue(draftValue, acknowledgedValue);
  const isEmptySecretDraft =
    isSensitive && (typeof draftValue !== 'string' || draftValue.length === 0);
  const hasPersistableChange = isDirty && !isEmptySecretDraft;
  const normalizeForPersistence = useCallback(
    (value: IntakeFieldValue): IntakeFieldValue => {
      if ((dataType === 'integer' || dataType === 'number') && typeof value === 'string') {
        return value.trim() === '' ? '' : Number(value);
      }
      if ((dataType === 'multi-enum' || dataType === 'multi-string') && typeof value === 'string') {
        return value
          .split(',')
          .map(entry => entry.trim())
          .filter(Boolean);
      }
      return value;
    },
    [dataType],
  );
  const persist = useCallback(
    async (value: IntakeFieldValue) => {
      try {
        await onPersist(field, normalizeForPersistence(value));
        if (isSensitive && typeof value === 'string') {
          setSessionSavedSecret(value);
          setIsReplacingSecret(false);
          setIsSecretVisible(false);
        }
        if (isEditingSuggestionRef.current && suggestionRef.current) {
          discardSuggestionRef.current?.(suggestionRef.current);
        }
      } catch {
        // Keep backend bodies and secret values out of the shared autosave
        // hook's console error while still preserving a retryable failure.
        throw new Error('Unable to save intake field');
      }
    },
    [field, isSensitive, normalizeForPersistence, onPersist],
  );
  const {
    saveImmediately,
    triggerAutoSave,
    hasPendingSave,
    cancelPendingSave,
    status,
    lastSavedAt,
  } = useAutoSave<IntakeFieldValue>({
    onSave: persist,
    debounceMs: 800,
    enabled: saveMode === 'autosave' && !disabled && isWritable,
    flushOnUnmount: saveMode === 'autosave',
  });

  const displayedValue = useMemo(() => {
    if (!isSensitive) return draftValue;
    if (isReplacingSecret) return typeof draftValue === 'string' ? draftValue : '';
    if (typeof draftValue === 'string') return draftValue;
    if (sessionSavedSecret !== null) return sessionSavedSecret;
    if (acknowledgedValue === true) return '';
    return draftValue;
  }, [acknowledgedValue, draftValue, isReplacingSecret, isSensitive, sessionSavedSecret]);

  const saveLatest = useCallback(() => {
    if (isEmptySecretDraft) {
      cancelPendingSave();
      if (isReplacingSecret) setIsReplacingSecret(false);
      return;
    }
    const error = getIntakeFieldFormatError(field, draftValue, acknowledgedValue);
    setBlurValidationError(error);
    if (error) {
      cancelPendingSave();
    } else if (isWritable && isDirty) {
      saveImmediately(draftValue);
    }
  }, [
    acknowledgedValue,
    cancelPendingSave,
    draftValue,
    field,
    isDirty,
    isEmptySecretDraft,
    isReplacingSecret,
    isWritable,
    saveImmediately,
  ]);

  useEffect(() => {
    onSaveStateChange?.(
      field,
      {
        status,
        isDirty: hasPersistableChange,
        lastSavedAt,
        retry: saveLatest,
      },
      saveStateKey,
    );
  }, [
    field,
    hasPersistableChange,
    lastSavedAt,
    onSaveStateChange,
    saveLatest,
    saveStateKey,
    status,
  ]);

  const hasHydratedSecretOnFile =
    isSensitive && acknowledgedValue === true && sessionSavedSecret === null && !isReplacingSecret;

  const pendingSuggestion =
    !isSensitive && suggestion && !isSameValue(draftValue, suggestion.value) ? suggestion : null;
  const showsReplacement =
    Boolean(pendingSuggestion) &&
    hasDisplayedValue(draftValue) &&
    (isDirty || hasAcknowledgedValue !== false);
  const suggestionControlsDisabled =
    disabled || isAcceptingSuggestion || hasPendingSave() || status === 'saving';

  useEffect(() => {
    setIsEditingSuggestion(false);
    setSuggestionError(null);
    manualEditBaselineRef.current = undefined;
  }, [suggestion?.extraction_id, suggestion?.field_id]);

  useEffect(() => {
    if (isEditingSuggestion) {
      controlRootRef.current?.querySelector<HTMLElement>(EDITABLE_CONTROL_SELECTOR)?.focus();
      return;
    }
    if (shouldRestoreSuggestionFocusRef.current) {
      shouldRestoreSuggestionFocusRef.current = false;
      editSuggestionButtonRef.current?.focus();
    }
  }, [isEditingSuggestion]);

  const activeSuggestion = isEditingSuggestion ? null : pendingSuggestion;
  const replacementDiff = useMemo(() => {
    if (!pendingSuggestion || !showsReplacement) return null;
    return diffWords(
      displayIntakeFieldValue(field, draftValue),
      displayIntakeFieldValue(field, pendingSuggestion.value),
    );
  }, [draftValue, field, pendingSuggestion, showsReplacement]);

  const suggestionValueSx = {
    color: wf.ink,
    fontSize: '12px',
    lineHeight: 1.45,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  } as const;

  const acceptPendingSuggestion = () => {
    if (!pendingSuggestion || !onAcceptSuggestion) return;
    const formatError = getIntakeFieldFormatError(
      field,
      pendingSuggestion.value,
      acknowledgedValue,
    );
    if (formatError) {
      setBlurValidationError(formatError);
      onDiscardSuggestion?.(pendingSuggestion);
      onDraftChange(field, pendingSuggestion.value);
      return;
    }
    setSuggestionError(null);
    setIsAcceptingSuggestion(true);
    void onAcceptSuggestion(pendingSuggestion)
      .then(() => onDraftChange(field, pendingSuggestion.value))
      .catch((error: unknown) => setSuggestionError(suggestionAcceptanceFailureKind(error)))
      .finally(() => setIsAcceptingSuggestion(false));
  };

  const cancelManualEditing = () => {
    cancelPendingSave();
    onDraftChange(field, manualEditBaselineRef.current ?? acknowledgedValue);
    manualEditBaselineRef.current = undefined;
    shouldRestoreSuggestionFocusRef.current = true;
    isCancellingManualEditRef.current = false;
    setIsEditingSuggestion(false);
  };

  const handleAutosaveBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (
      isEditingSuggestion &&
      (isCancellingManualEditRef.current ||
        event.relatedTarget === cancelManualEditButtonRef.current)
    ) {
      return;
    }
    saveLatest();
  };

  const suggestionControl = pendingSuggestion ? (
    <Box role="group" aria-label={`Suggested value for ${field.label}`}>
      {!showsReplacement && (
        <Box sx={{ color: wf.ink, fontSize: '11px', fontWeight: 650, mb: 0.5 }}>{field.label}</Box>
      )}
      {showsReplacement ? (
        <Box
          sx={{
            overflow: 'hidden',
            border: `1px solid ${wf.line}`,
            borderTop: `1px dashed ${wf.purple}`,
            borderRadius: '0 0 7px 7px',
            backgroundColor: wf.purpleSoft,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'minmax(0, 1fr) 32px minmax(0, 1fr)',
              },
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              px: { xs: 1.25, sm: 1.5 },
              py: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  color: wf.faint,
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  mb: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                Current
              </Box>
              <Box
                sx={suggestionValueSx}
                aria-label={`Current value: ${displayIntakeFieldValue(field, draftValue)}`}
              >
                <DiffValue tokens={replacementDiff?.current ?? []} />
              </Box>
            </Box>
            <Box
              aria-hidden="true"
              sx={{
                color: wf.purple,
                fontSize: '20px',
                lineHeight: 1,
                textAlign: 'center',
                transform: { xs: 'rotate(90deg)', sm: 'none' },
              }}
            >
              →
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  color: wf.purple,
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  mb: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                Suggested
              </Box>
              <Box
                sx={suggestionValueSx}
                aria-label={`Suggested value: ${displayIntakeFieldValue(field, pendingSuggestion.value)}`}
              >
                <DiffValue tokens={replacementDiff?.suggested ?? []} />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              borderTop: `1px solid ${wf.lineSoft}`,
              backgroundColor: wf.surface,
              px: { xs: 1.25, sm: 1.5 },
              py: 1,
            }}
          >
            <Box sx={{ color: wf.muted, fontSize: '10px', mr: 'auto' }}>
              From {suggestionSource(pendingSuggestion.document_kind)}
            </Box>
            <Button
              ref={editSuggestionButtonRef}
              size="small"
              variant="text"
              onClick={() => {
                manualEditBaselineRef.current = draftValue;
                setIsEditingSuggestion(true);
              }}
              disabled={suggestionControlsDisabled}
              aria-label={`Edit ${field.label} manually`}
              sx={{ minWidth: 0, px: 0.75, textDecoration: 'underline', textTransform: 'none' }}
            >
              Edit manually
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => onDiscardSuggestion?.(pendingSuggestion)}
              disabled={suggestionControlsDisabled || !onDiscardSuggestion}
              aria-label={`Keep current value for ${field.label}`}
              sx={{ minWidth: 0, color: wf.muted, px: 0.75, textTransform: 'none' }}
            >
              Keep current
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={acceptPendingSuggestion}
              disabled={suggestionControlsDisabled || !onAcceptSuggestion}
              aria-label={`Use suggested value for ${field.label}`}
              sx={{
                backgroundColor: wf.purple,
                textTransform: 'none',
                '&:hover': { backgroundColor: wf.purple },
              }}
            >
              {isAcceptingSuggestion ? 'Using…' : 'Use suggested'}
            </Button>
          </Box>
          {suggestionError && (
            <Box role="alert" sx={{ color: 'error.main', fontSize: '10px', mt: 0.75 }}>
              {suggestionError === 'stale'
                ? 'This suggestion changed. Review the refreshed document and try again.'
                : "We couldn't confirm this suggestion. Review the refreshed document and try again."}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            overflow: 'hidden',
            border: `1px solid ${wf.line}`,
            borderLeft: `2px solid ${wf.purple}`,
            borderRadius: '6px',
            backgroundColor: wf.surface,
          }}
        >
          <Box
            sx={{
              minHeight: 36,
              display: 'flex',
              alignItems: 'center',
              color: wf.faint,
              fontSize: '12px',
              px: 1.25,
              py: 0.75,
            }}
          >
            Empty
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              backgroundColor: wf.purpleSoft,
              borderTop: `1px solid ${wf.lineSoft}`,
              px: 1.25,
              py: 0.75,
            }}
          >
            <Box sx={{ ...suggestionValueSx, flex: '1 1 120px' }}>
              {displayIntakeFieldValue(field, pendingSuggestion.value)}
            </Box>
            <Box sx={{ color: wf.muted, fontSize: '10px' }}>
              From {suggestionSource(pendingSuggestion.document_kind)}
            </Box>
            <Button
              size="small"
              variant="text"
              onClick={() => onDiscardSuggestion?.(pendingSuggestion)}
              disabled={disabled || isAcceptingSuggestion || !onDiscardSuggestion}
              aria-label={`Discard suggested value for ${field.label}`}
              sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: '10px', textTransform: 'none' }}
            >
              Dismiss
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={acceptPendingSuggestion}
              disabled={disabled || isAcceptingSuggestion || !onAcceptSuggestion}
              aria-label={`Accept suggested value for ${field.label}`}
              sx={{
                minWidth: 0,
                backgroundColor: wf.purple,
                px: 1.25,
                py: 0.25,
                fontSize: '10px',
                textTransform: 'none',
                '&:hover': { backgroundColor: wf.purple },
              }}
            >
              {isAcceptingSuggestion ? 'Accepting…' : 'Accept'}
            </Button>
          </Box>
          {suggestionError && (
            <Box role="alert" sx={{ color: 'error.main', fontSize: '10px', px: 1.25, py: 0.75 }}>
              {suggestionError === 'stale'
                ? 'This suggestion changed. Review the refreshed document and try again.'
                : "We couldn't confirm this suggestion. Review the refreshed document and try again."}
            </Box>
          )}
        </Box>
      )}
    </Box>
  ) : null;

  const wrapControl = (control: ReactNode) => (
    <Box
      sx={
        activeSuggestion && showsReplacement
          ? {
              '& .MuiOutlinedInput-root': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
            }
          : { display: 'grid' }
      }
    >
      <Box
        ref={controlRootRef}
        aria-hidden={activeSuggestion ? true : undefined}
        inert={activeSuggestion ? true : undefined}
        sx={
          activeSuggestion && !showsReplacement
            ? { gridArea: '1 / 1', opacity: 0, pointerEvents: 'none' }
            : activeSuggestion
              ? { pointerEvents: 'none' }
              : undefined
        }
      >
        {control}
        {isEditingSuggestion && suggestion && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
            <Button
              ref={cancelManualEditButtonRef}
              size="small"
              variant="text"
              onPointerDown={() => {
                isCancellingManualEditRef.current = true;
              }}
              onClick={cancelManualEditing}
              disabled={disabled || status === 'saving'}
              aria-label={`Cancel manual edit for ${field.label}`}
              sx={{ minWidth: 0, px: 0.75, textDecoration: 'underline', textTransform: 'none' }}
            >
              Cancel manual edit
            </Button>
          </Box>
        )}
      </Box>
      {activeSuggestion && (
        <Box
          sx={
            showsReplacement
              ? { minWidth: 0, mt: '-1px', position: 'relative', zIndex: 1 }
              : { gridArea: '1 / 1', minWidth: 0, zIndex: 1 }
          }
        >
          {suggestionControl}
        </Box>
      )}
    </Box>
  );

  const displayedValidationError = validationError ?? blurValidationError;
  const statusControl =
    !showStatus || (activeSuggestion && showsReplacement) ? null : displayedValidationError ? (
      <Box
        aria-live="polite"
        sx={{ minHeight: 28, color: 'error.main', fontSize: '0.75rem', textAlign: 'right' }}
      >
        Not saved
      </Box>
    ) : saveMode === 'explicit' ? (
      <Box
        aria-live="polite"
        sx={{ minHeight: 28, color: 'text.secondary', fontSize: '0.75rem', textAlign: 'right' }}
      >
        {hasPersistableChange ? 'Unsaved change' : null}
      </Box>
    ) : (
      <Box
        aria-live="polite"
        sx={{
          display: 'flex',
          minHeight: 28,
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <AutosaveStatus
          status={status}
          isDirty={hasPersistableChange}
          lastSavedAt={lastSavedAt}
          messages={{ error: 'Could not save this field' }}
          verbose
        />
        {status === 'error' && (
          <Button size="small" onClick={saveLatest} disabled={disabled}>
            Retry save
          </Button>
        )}
      </Box>
    );

  if (dataType === 'file') return null;

  if (disabledReason) {
    return wrapControl(
      <Alert severity="warning" aria-label={`${field.label} unavailable`}>
        <Box sx={{ fontWeight: 600 }}>{field.label}</Box>
        {maskedValue && <Box sx={{ fontFamily: 'monospace' }}>{`${maskedValue} · on file`}</Box>}
        <Box>{disabledReason}</Box>
      </Alert>,
    );
  }

  if (!isWritable) {
    const reason =
      field.destination.kind === 'secure_collection_deferred' ? field.destination.reason : null;
    const providerRosterMessage =
      field.destination.kind === 'provider_roster'
        ? 'Your JotPsych specialist manages this provider roster with your team.'
        : null;
    return wrapControl(
      <Alert severity="info" aria-label={`${field.label} unavailable`}>
        <Box sx={{ fontWeight: 600 }}>{field.label}</Box>
        <Box>
          {reason ??
            providerRosterMessage ??
            'This information is not available for editing in Intake Hub yet.'}
        </Box>
      </Alert>,
    );
  }

  if (hasHydratedSecretOnFile) {
    return wrapControl(
      <Box>
        <Box
          role="status"
          sx={{
            minHeight: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {maskedValue ? (
              <>
                {`${field.label}: `}
                <Box component="span" sx={{ color: 'text.primary', fontFamily: 'monospace' }}>
                  {maskedValue}
                </Box>
                {' · on file'}
              </>
            ) : (
              `${field.label} saved securely`
            )}
          </Box>
          <Button
            size="small"
            variant="text"
            aria-label={`Replace ${field.label}`}
            disabled={disabled}
            onClick={() => {
              cancelPendingSave();
              setIsSecretVisible(false);
              setIsReplacingSecret(true);
            }}
          >
            Replace
          </Button>
        </Box>
        {field.help_text && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
            {field.help_text}
          </Box>
        )}
      </Box>,
    );
  }

  if (isSpecialtyField(field)) {
    const specialtyValue = typeof displayedValue === 'string' ? displayedValue : '';
    return wrapControl(
      <Box>
        <Box sx={readOnly ? READONLY_WRAPPER_SX : undefined} aria-readonly={readOnly || undefined}>
          <SpecialtySelect
            disabled={disabled}
            error={Boolean(displayedValidationError)}
            helperText={displayedValidationError || field.help_text || undefined}
            label={field.label}
            required={field.is_required}
            value={specialtyValue}
            onChange={specialty => {
              if (readOnly) return;
              const nextValue = specialty?.specialty_name ?? '';
              onDraftChange(field, nextValue);
              if (saveMode === 'autosave') saveImmediately(nextValue);
            }}
          />
        </Box>
        {statusControl}
      </Box>,
    );
  }

  if (dataType === 'boolean') {
    return wrapControl(
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(draftValue)}
              disabled={disabled}
              required={field.is_required}
              slotProps={{ input: { readOnly, 'aria-readonly': readOnly } }}
              sx={readOnly ? { pointerEvents: 'none', opacity: 0.72 } : undefined}
              onChange={event => {
                if (readOnly) return;
                const nextValue = event.target.checked;
                onDraftChange(field, nextValue);
                if (saveMode === 'autosave') saveImmediately(nextValue);
              }}
            />
          }
          label={field.label}
        />
        {field.help_text && !(activeSuggestion && showsReplacement) && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.75rem', ml: 4.5 }}>
            {field.help_text}
          </Box>
        )}
        {validationError && (
          <Box role="alert" sx={{ color: 'error.main', fontSize: '0.75rem', ml: 4.5 }}>
            {validationError}
          </Box>
        )}
        {statusControl}
      </Box>,
    );
  }

  if (isCommonCptCodesField(field)) {
    const selectedCodes = Array.isArray(displayedValue)
      ? uniqueStoredCodes(displayedValue)
      : typeof displayedValue === 'string'
        ? uniqueStoredCodes(displayedValue.split(','))
        : [];
    const selectedOptions: CPTCodeOption[] = selectedCodes.map(
      code =>
        CPT_CODES.find(option => option.cpt === code) ?? {
          cpt: code,
          description: 'Saved CPT code',
          category: 'Other',
        },
    );

    return wrapControl(
      <Box>
        <Autocomplete<CPTCodeOption, true, false, true>
          multiple
          freeSolo
          disableCloseOnSelect
          filterSelectedOptions
          disabled={disabled}
          readOnly={readOnly}
          options={CPT_CODES}
          value={selectedOptions}
          getOptionLabel={option =>
            typeof option === 'string' ? option : `${option.cpt} - ${option.description}`
          }
          isOptionEqualToValue={(option, value) =>
            typeof value !== 'string' && option.cpt === value.cpt
          }
          onChange={(_event, options) => {
            if (readOnly) return;
            const selectedCodesByKey = new Map(
              selectedCodes.map(code => [code.toUpperCase(), code]),
            );
            const nextValue = uniqueStoredCodes(
              options.flatMap(option => {
                const rawCode = typeof option === 'string' ? option : option.cpt;
                const storedCode = selectedCodesByKey.get(rawCode.trim().toUpperCase());
                return storedCode ? [storedCode] : uniqueBillableCodes([rawCode]);
              }),
            );
            onDraftChange(field, nextValue);
            if (saveMode === 'autosave') saveImmediately(nextValue);
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box component="li" key={key} {...optionProps}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{option.cpt}</Box>
                  <Box sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {option.description}
                  </Box>
                </Box>
              </Box>
            );
          }}
          renderValue={(selected, getItemProps) =>
            selected.map((option, index) => {
              const code = typeof option === 'string' ? option : option.cpt;
              const { key, ...chipProps } = getItemProps({ index });
              return (
                <Chip
                  key={key}
                  label={code}
                  size="small"
                  deleteIcon={<Close aria-label={`Remove CPT code ${code}`} />}
                  {...chipProps}
                />
              );
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              label={field.label}
              placeholder={selectedCodes.length === 0 ? 'Search CPT codes' : undefined}
              required={field.is_required && selectedCodes.length === 0}
              size="small"
            />
          )}
        />
        {field.help_text && !(activeSuggestion && showsReplacement) && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
            {field.help_text}
          </Box>
        )}
        {statusControl}
      </Box>,
    );
  }

  if (dataType === 'state') {
    const stateValue = typeof displayedValue === 'string' ? displayedValue : '';
    const isLegacyValue =
      stateValue.length > 0 && !Object.prototype.hasOwnProperty.call(US_STATES, stateValue);

    return wrapControl(
      <Box>
        <FormControl
          fullWidth
          size="small"
          required={field.is_required}
          disabled={disabled}
          error={Boolean(displayedValidationError)}
        >
          <InputLabel id={`${field.field_id}-state-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.field_id}-state-label`}
            label={field.label}
            value={stateValue}
            readOnly={readOnly}
            sx={readOnly ? { pointerEvents: 'none' } : undefined}
            onChange={event => {
              if (readOnly) return;
              const nextValue = event.target.value;
              onDraftChange(field, nextValue);
              if (saveMode === 'autosave') saveImmediately(nextValue);
            }}
          >
            <MenuItem value="">
              <em>Select a state</em>
            </MenuItem>
            {isLegacyValue && <MenuItem value={stateValue}>{stateValue} (saved value)</MenuItem>}
            {STATE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {field.help_text && !(activeSuggestion && showsReplacement) && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
            {field.help_text}
          </Box>
        )}
        {displayedValidationError && (
          <Box role="alert" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
            {displayedValidationError}
          </Box>
        )}
        {statusControl}
      </Box>,
    );
  }

  if ((field.options?.length ?? 0) > 0) {
    const isMultiple = dataType === 'multi-enum';
    const selectValue = isMultiple
      ? Array.isArray(draftValue)
        ? draftValue
        : []
      : Array.isArray(draftValue)
        ? (draftValue[0] ?? '')
        : draftValue;
    const selectedRawValues = Array.isArray(selectValue) ? selectValue : [selectValue];
    const legacyOptions = selectedRawValues.filter(
      (value): value is string =>
        typeof value === 'string' && value.length > 0 && !field.options?.includes(value),
    );

    return wrapControl(
      <Box>
        <FormControl
          fullWidth
          size="small"
          required={field.is_required}
          disabled={disabled}
          error={Boolean(validationError)}
        >
          <InputLabel id={`${field.field_id}-options-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.field_id}-options-label`}
            label={field.label}
            multiple={isMultiple}
            value={selectValue}
            readOnly={readOnly}
            sx={readOnly ? { pointerEvents: 'none' } : undefined}
            renderValue={value => displayIntakeFieldValue(field, value)}
            onChange={event => {
              if (readOnly) return;
              const nextValue = event.target.value;
              onDraftChange(field, nextValue);
              if (saveMode === 'autosave') saveImmediately(nextValue);
            }}
          >
            {field.options?.map(option => (
              <MenuItem key={option} value={option}>
                {displayIntakeFieldValue(field, option)}
              </MenuItem>
            ))}
            {legacyOptions.map(option => (
              <MenuItem key={option} value={option}>
                {displayIntakeFieldValue(field, option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {field.help_text && !(activeSuggestion && showsReplacement) && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
            {field.help_text}
          </Box>
        )}
        {validationError && (
          <Box role="alert" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
            {validationError}
          </Box>
        )}
        {statusControl}
      </Box>,
    );
  }

  const inputType = isSecret && isSecretVisible ? 'text' : fieldControlType(dataType);
  const isEmptyGeneratedDate = inputType === 'date' && !hasDisplayedValue(displayedValue);

  return wrapControl(
    <Box>
      <TextField
        fullWidth
        size="small"
        sx={inputType === 'date' ? EMPTY_GENERATED_DATE_SX : undefined}
        label={field.label}
        type={inputType}
        value={
          typeof displayedValue === 'boolean'
            ? String(displayedValue)
            : Array.isArray(displayedValue)
              ? displayedValue.join(', ')
              : displayedValue
        }
        required={field.is_required}
        disabled={disabled}
        error={Boolean(displayedValidationError)}
        helperText={
          activeSuggestion && showsReplacement
            ? undefined
            : displayedValidationError || field.help_text || undefined
        }
        onKeyDown={event => {
          if (isNumeric) {
            const disallowedKeys = isInteger ? ['e', 'E', '+', '.'] : ['e', 'E', '+'];
            if (disallowedKeys.includes(event.key)) event.preventDefault();
            return;
          }
          if (
            isPhoneOrFax &&
            event.key.length === 1 &&
            !event.ctrlKey &&
            !event.metaKey &&
            !PHONE_ALLOWED_CHAR.test(event.key)
          ) {
            event.preventDefault();
          }
        }}
        onPaste={event => {
          if (!isNumeric) return;
          const pastedValue = event.clipboardData.getData('text');
          const numericPattern = isInteger ? /^-?\d+$/ : /^-?(?:\d+\.?\d*|\.\d+)$/;
          if (!numericPattern.test(pastedValue.trim())) event.preventDefault();
        }}
        onChange={event => {
          const input = event.target as HTMLInputElement;
          const numberValue = input.valueAsNumber;
          const nextValue: IntakeFieldValue =
            inputType === 'number' && input.value !== '' && !Number.isNaN(numberValue)
              ? numberValue
              : isPhoneOrFax
                ? input.value.replace(PHONE_DISALLOWED_CHARS, '')
                : input.value;
          onDraftChange(field, nextValue);
          const nextError = getIntakeFieldFormatError(field, nextValue, acknowledgedValue);
          if (blurValidationError) setBlurValidationError(nextError);
          if (
            nextError ||
            (isSensitive && typeof nextValue === 'string' && nextValue.length === 0)
          ) {
            cancelPendingSave();
          } else if (saveMode === 'autosave' && !IMMEDIATE_SAVE_TYPES.has(dataType)) {
            if (isSameValue(normalizeForPersistence(nextValue), acknowledgedValue)) {
              cancelPendingSave();
            } else {
              triggerAutoSave(nextValue);
            }
          }
        }}
        onBlur={saveMode === 'autosave' ? handleAutosaveBlur : undefined}
        slotProps={{
          htmlInput: isSensitive
            ? {
                autoComplete: 'new-password',
                'aria-label': field.label,
                'data-private': 'true',
                'data-sentry-mask': true,
                ...(readOnly ? { readOnly: true } : {}),
              }
            : {
                'aria-label': field.label,
                ...(readOnly ? { readOnly: true } : {}),
                ...(isEmptyGeneratedDate ? { 'data-empty-generated-date': 'true' } : {}),
                ...(isNumeric ? { inputMode: isInteger ? 'numeric' : 'decimal' } : {}),
                ...(!isNumeric && isPhoneOrFax ? { inputMode: 'tel' } : {}),
                ...(isInteger ? { step: 1 } : {}),
              },
          inputLabel:
            inputType === 'date' || hasDisplayedValue(displayedValue)
              ? { shrink: true }
              : undefined,
          formHelperText: displayedValidationError ? { role: 'alert' } : undefined,
          input: isSecret
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={isSecretVisible ? `Hide ${field.label}` : `Show ${field.label}`}
                    >
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={isSecretVisible ? `Hide ${field.label}` : `Show ${field.label}`}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => setIsSecretVisible(visible => !visible)}
                      >
                        {isSecretVisible ? (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }
            : undefined,
        }}
      />
      {statusControl}
    </Box>,
  );
}

export const IntakePageFieldInput = memo(IntakePageFieldInputComponent);
